import React, { useCallback, useEffect, useState } from 'react';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { getApiBaseUrl } from '../../../../core/config/api';

interface RankingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RankingType = 'niveau' | 'argent' | 'interventions';

interface RankingEntry {
  rank: number;
  user_id: number;
  username: string;
  niveau: number;
  xp: number;
  argents: number;
  interventions: number;
  premium: number;
  value: number;
  is_me: boolean;
}

const TABS: RankingType[] = ['niveau', 'argent', 'interventions'];

const RankingModal: React.FC<RankingModalProps> = ({ isOpen, onClose }) => {
  const { t } = usePageTranslation('game', 'Dashboard');

  const [activeTab, setActiveTab] = useState<RankingType>('niveau');
  const [entries, setEntries] = useState<RankingEntry[]>([]);
  const [me, setMe] = useState<RankingEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = useCallback(async (type: RankingType) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/ranking?type=${type}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      });
      const data = await res.json();
      if (data?.success) {
        setEntries(data.data?.entries ?? []);
        setMe(data.data?.me ?? null);
      } else {
        setError(t('rankingModal.error'));
      }
    } catch {
      setError(t('rankingModal.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // (Re)charger à l'ouverture et à chaque changement d'onglet.
  useEffect(() => {
    if (isOpen) fetchRanking(activeTab);
  }, [isOpen, activeTab, fetchRanking]);

  // Fermer avec Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatValue = (entry: RankingEntry): string => {
    switch (activeTab) {
      case 'argent':
        return `${entry.argents.toLocaleString('fr-FR')} €`;
      case 'interventions':
        return entry.interventions.toLocaleString('fr-FR');
      case 'niveau':
      default:
        return `${t('rankingModal.level')} ${entry.niveau}`;
    }
  };

  const rankBadge = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const renderRow = (entry: RankingEntry, highlightMe = true) => (
    <div
      key={entry.user_id}
      className={`flex items-center gap-3 px-4 py-2.5 ${
        highlightMe && entry.is_me
          ? 'bg-amber-500/15 border-l-2 border-amber-400'
          : 'border-l-2 border-transparent hover:bg-gray-800/40'
      } transition-colors`}
    >
      <div className={`w-10 shrink-0 text-center font-bold ${entry.rank <= 3 ? 'text-lg' : 'text-sm text-gray-400'}`}>
        {rankBadge(entry.rank)}
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="truncate font-medium text-white">{entry.username}</span>
        {entry.premium ? (
          <span className="shrink-0 rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-300">
            ★
          </span>
        ) : null}
        {entry.is_me && (
          <span className="shrink-0 rounded bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">
            {t('rankingModal.you')}
          </span>
        )}
      </div>
      <div className="shrink-0 text-right text-sm font-semibold text-amber-100 tabular-nums">
        {formatValue(entry)}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700 bg-gray-800/50">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              {t('ranking')}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{t('rankingModal.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            aria-label="Fermer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Onglets */}
        <div className="flex gap-1 p-2 border-b border-gray-700 bg-gray-800/30">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                  : 'text-gray-400 hover:bg-gray-800/60 border border-transparent'
              }`}
            >
              {t(`rankingModal.tabs.${tab}`)}
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-400 text-sm">{t('rankingModal.loading')}</div>
          ) : error ? (
            <div className="py-12 text-center text-red-300 text-sm">{error}</div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">{t('rankingModal.empty')}</div>
          ) : (
            <div className="divide-y divide-gray-800/60">{entries.map((e) => renderRow(e))}</div>
          )}
        </div>

        {/* Mon rang (épinglé en bas s'il n'est pas dans la liste visible) */}
        {me && !entries.some((e) => e.is_me) && (
          <div className="border-t border-gray-700 bg-gray-800/60">
            <div className="px-4 pt-2 text-[11px] uppercase tracking-wide text-gray-500">
              {t('rankingModal.yourRank')}
            </div>
            {renderRow(me)}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankingModal;
